import { useEffect, useRef, useState } from "react";
import { Plus, X, MessageSquareWarning, Sparkles, Loader2, EyeOff, TrendingDown } from "lucide-react";
import { SlideOver, Field } from "@/components/UI";
import { formatCurrency } from "@/lib/format";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { CURRENCIES, SIGNERS, empty, withKey } from "./constants";
import { SourcingIntelPanel, QuoteIssues, quoteIssues } from "./SpecSheetTools";
import { PriceBookSuggest } from "./PriceBookSuggest";

export default function DocEditor({ open, onClose, onSave, initial, docType, meta }) {
  // Ensure every line item has a stable _key (survives splice/reorder).
  // Both `initial` (from backend) and `empty()` seeds pass through here.
  const hydrate = (d) => {
    const base = d || empty(docType);
    return { ...base, items: (base.items || []).map(withKey) };
  };
  const [form, setForm] = useState(() => hydrate(initial));
  const lpo = docType === "lpo";
  const priced = docType === "quotation" || docType === "invoice" || lpo;
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    if (open && lpo) api.listSuppliers().then(setSuppliers).catch(() => {});
  }, [open, lpo]);
  const pickSupplier = (id) => {
    const sp = suppliers.find((x) => x.id === id);
    if (!sp) { setForm((f) => ({ ...f, supplier_id: "" })); return; }
    setForm((f) => ({
      ...f,
      supplier_id: sp.id,
      client_name: sp.name,
      client_address: sp.address || f.client_address,
      client_contact_person: sp.contact_person || f.client_contact_person,
      client_contact_phone: sp.phone || f.client_contact_phone,
      client_email: sp.email || f.client_email,
      client_branch: sp.tin || f.client_branch,
      payment_terms: sp.payment_terms || f.payment_terms,
    }));
    preloadSupplierItems(sp.id);
  };

  const preloadSupplierItems = async (supplierId) => {
    try {
      const r = await api.supplierLpoItems(supplierId);
      if (!r.items?.length) return;
      setForm((f) => {
        if ((f.items || []).some((it) => (it.description || "").trim())) return f;
        return {
          ...f,
          items: r.items.map((it) => withKey({
            description: it.description, unit_of_measure: it.unit_of_measure,
            qty: it.qty || 1, unit_price: it.unit_price || 0,
            supplied_by: "", serial_no: "", cost_price: 0, margin_pct: 0,
          })),
        };
      });
      toast(`Pre-loaded ${r.items.length} item(s) from ${r.supplier}'s catalogue — adjust quantities & prices`, "success");
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open && lpo && initial?.supplier_id && !initial?.id) preloadSupplierItems(initial.supplier_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => setForm(hydrate(initial)), [initial, docType, open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updItem = (i, patch) => setForm((f) => {
    const items = [...(f.items || [])];
    items[i] = { ...items[i], ...patch };
    return { ...f, items };
  });
  const addItem = () => setForm((f) => ({
    ...f,
    items: [...(f.items || []), withKey({ description: "", unit_of_measure: "Pcs", qty: 1, unit_price: 0, supplied_by: "Jero-One", serial_no: "" })],
  }));
  const delItem = (i) => setForm((f) => ({ ...f, items: (f.items || []).filter((_, idx) => idx !== i) }));

  // --- AI spec-sheet analysis + internal cost/margin pricing (quotations) ---
  const { toast } = useToast();
  const specInput = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [bulkMargin, setBulkMargin] = useState(30);
  const [bestMap, setBestMap] = useState({});
  const [findingBest, setFindingBest] = useState(false);
  useEffect(() => { setBestMap({}); }, [open]);

  const findBestPrices = async () => {
    setFindingBest(true);
    const map = {};
    await Promise.all((form.items || []).map(async (it, i) => {
      const q = (it.description || "").trim();
      if (q.length < 3) return;
      try {
        const res = await api.bestPrice(q);
        if (res.length && res[0].offers?.length) map[i] = res[0].offers[0];
      } catch { /* ignore */ }
    }));
    setBestMap(map);
    setFindingBest(false);
    const n = Object.keys(map).length;
    toast(n ? `Found cheapest supplier for ${n} item(s)` : "No supplier price history for these items yet — it builds up automatically from your LPOs", n ? "success" : "info");
  };
  useEffect(() => {
    if (open) setShowInternal((initial?.items || []).some((it) => (it.cost_price || 0) > 0));
  }, [initial, open]);

  const handleSpec = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    toast("Analyzing spec sheet & researching market prices — this can take a minute…", "info");
    try {
      const { job_id } = await api.specAnalyze(file, bulkMargin || 30);
      let job = { status: "processing" };
      const deadline = Date.now() + 5 * 60 * 1000;
      while (job.status === "processing" && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        job = await api.specAnalyzeStatus(job_id);
      }
      if (job.status !== "done") throw new Error(job.error || "Analysis timed out — try again.");
      const res = job.result;
      setForm((f) => ({
        ...f,
        title: f.title || res.title || "",
        items: (res.items || []).map(withKey),
        sourcing_intel: res.sourcing_intel || [],
        spec_summary: res.summary || "",
      }));
      setShowInternal(true);
      toast(`Extracted ${res.items?.length || 0} items with market pricing`, "success");
    } catch (e) {
      toast(e?.response?.data?.detail || e.message || "Spec sheet analysis failed", "error");
    } finally {
      setAnalyzing(false);
      if (specInput.current) specInput.current.value = "";
    }
  };

  const updPricing = (i, field, val) => setForm((f) => {
    const items = [...(f.items || [])];
    const it = { ...items[i] };
    const num = parseFloat(val) || 0;
    if (field === "cost_price") {
      it.cost_price = num;
      if (num > 0) it.unit_price = Math.round(num * (1 + (parseFloat(it.margin_pct) || 0) / 100));
    } else if (field === "margin_pct") {
      it.margin_pct = num;
      if ((parseFloat(it.cost_price) || 0) > 0) it.unit_price = Math.round(it.cost_price * (1 + num / 100));
    } else if (field === "unit_price") {
      it.unit_price = num;
      const c = parseFloat(it.cost_price) || 0;
      if (c > 0) it.margin_pct = Math.round(((num / c) - 1) * 1000) / 10;
    }
    items[i] = it;
    return { ...f, items };
  });

  const applyBulkMargin = () => {
    const m = parseFloat(bulkMargin) || 0;
    setForm((f) => ({
      ...f,
      items: (f.items || []).map((it) => {
        const c = parseFloat(it.cost_price) || 0;
        return c > 0 ? { ...it, margin_pct: m, unit_price: Math.round(c * (1 + m / 100)) } : it;
      }),
    }));
    toast(`Applied ${m}% margin to all costed items`, "success");
  };

  // Convert all item prices when the quotation currency changes (live FX rates)
  const fxRef = useRef(null);
  const roundFor = (cur, v) =>
    cur === "UGX" ? Math.round(v / 100) * 100
    : cur === "KES" ? Math.round(v)
    : Math.round(v * 100) / 100;
  const changeCurrency = async (newCur) => {
    const oldCur = form.currency;
    if (newCur === oldCur) return;
    const hasPrices = (form.items || []).some(
      (it) => (parseFloat(it.unit_price) || 0) > 0 || (parseFloat(it.cost_price) || 0) > 0
    );
    if (!hasPrices) { upd({ currency: newCur }); return; }
    try {
      if (!fxRef.current) fxRef.current = await api.fxRates();
      const rates = fxRef.current.rates || {};
      const from = rates[oldCur], to = rates[newCur];
      if (!from || !to) { upd({ currency: newCur }); return; }
      const factor = to / from;
      setForm((f) => ({
        ...f,
        currency: newCur,
        items: (f.items || []).map((it) => ({
          ...it,
          unit_price: roundFor(newCur, (parseFloat(it.unit_price) || 0) * factor),
          cost_price: roundFor(newCur, (parseFloat(it.cost_price) || 0) * factor),
        })),
      }));
      const rate = factor >= 1 ? factor.toFixed(factor >= 100 ? 0 : 2) : (1 / factor).toFixed(2);
      toast(
        `Prices converted ${oldCur} → ${newCur} @ ${factor >= 1 ? `1 ${oldCur} = ${rate} ${newCur}` : `${rate} ${oldCur} = 1 ${newCur}`}`,
        "success"
      );
    } catch {
      upd({ currency: newCur });
      toast("Live FX rates unavailable — currency changed without converting prices", "error");
    }
  };

  const sub = (form.items || []).reduce((a, it) => a + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const vat = (sub * (parseFloat(form.tax_rate) || 0)) / 100;
  const tot = sub + vat;
  const totalCost = (form.items || []).reduce((a, it) => a + (parseFloat(it.qty) || 0) * (parseFloat(it.cost_price) || 0), 0);
  const profit = sub - totalCost;
  const errors = priced ? quoteIssues(form) : [];

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={initial?.id ? `Edit ${meta.short}` : `New ${meta.short}`}
      subtitle={initial?.serial || "Auto-numbered on save"}
      footer={
        <>
          <button onClick={onClose} className="btn btn-ghost" data-testid="cancel-edit">Cancel</button>
          <button onClick={() => onSave(form)} className="btn btn-ghost" data-testid="save-doc">
            {initial?.id ? "Save" : `Create`}
          </button>
          <button
            onClick={() => onSave(form, { thenAction: "download" })}
            className="btn btn-primary"
            data-testid="save-download"
            title="Save then download PDF"
          >
            Save & Download PDF
          </button>
          <button
            onClick={() => onSave(form, { thenAction: "print" })}
            className="btn btn-soft"
            data-testid="save-print"
            title="Save then open print dialog"
          >
            Save & Print
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {priced && initial?.change_request && (
          <div
            data-testid="change-request-banner"
            className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3"
          >
            <MessageSquareWarning size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold text-amber-800">
                Client requested changes
                {initial.change_requested_at && (
                  <span className="font-normal text-xs text-amber-700">
                    {" "}· {new Date(initial.change_requested_at).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-amber-800 mt-1 whitespace-pre-wrap">“{initial.change_request}”</p>
              <p className="text-xs text-amber-700 mt-1.5">
                Revise below, save, then use the mail button to resend the updated quotation.
              </p>
            </div>
          </div>
        )}
        <Section title={lpo ? "Supplier / Contractor" : "Client / Recipient"}>
          {lpo && (
            <Field label="Pick from supplier directory">
              <select data-testid="lpo-supplier-select" className="select"
                value={form.supplier_id || ""} onChange={(e) => pickSupplier(e.target.value)}>
                <option value="">— Select a supplier (auto-fills below) —</option>
                {suppliers.map((sp) => (<option key={sp.id} value={sp.id}>{sp.name}</option>))}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={lpo ? "Supplier name *" : "Client name *"}>
              <input data-testid="client-name" className="input" value={form.client_name} onChange={(e) => upd({ client_name: e.target.value })} />
            </Field>
            <Field label={lpo ? "Supplier TIN" : "Branch"}>
              <input className="input" value={form.client_branch} onChange={(e) => upd({ client_branch: e.target.value })} />
            </Field>
            <Field label="Contact person">
              <input className="input" value={form.client_contact_person} onChange={(e) => upd({ client_contact_person: e.target.value })} />
            </Field>
            <Field label="Phone">
              <input className="input" value={form.client_contact_phone} onChange={(e) => upd({ client_contact_phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <input className="input" value={form.client_email} onChange={(e) => upd({ client_email: e.target.value })} />
            </Field>
            <Field label={lpo ? "Deliver to (location)" : "Location"}>
              <input className="input" value={form.project_location} onChange={(e) => upd({ project_location: e.target.value })} />
            </Field>
          </div>
          <Field label="Address">
            <input className="input" value={form.client_address} onChange={(e) => upd({ client_address: e.target.value })} />
          </Field>
        </Section>

        <Section title="Details">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Subject / Title *">
              <input data-testid="doc-title" className="input" value={form.title} onChange={(e) => upd({ title: e.target.value })} />
            </Field>
            <Field label="Date">
              <input type="date" className="input" value={form.date} onChange={(e) => upd({ date: e.target.value })} />
            </Field>
            <Field label="Prepared by">
              <select
                data-testid="prepared-by"
                className="select"
                value={form.prepared_by || ""}
                onChange={(e) => upd({ prepared_by: e.target.value })}
              >
                <option value="">Default (Director)</option>
                {SIGNERS.map((p) => (
                  <option key={p.name} value={p.name}>{`${p.name} — ${p.title}`}</option>
                ))}
              </select>
            </Field>
            <Field label="Signing officer">
              <select
                data-testid="signed-by"
                className="select"
                value={form.signed_by || ""}
                onChange={(e) => upd({ signed_by: e.target.value })}
              >
                <option value="">Default (Director)</option>
                {SIGNERS.map((p) => (
                  <option key={p.name} value={p.name}>{`${p.name} — ${p.title}`}</option>
                ))}
              </select>
            </Field>
            {priced && !lpo && (
              <Field label="Bank account (on PDF)">
                <select
                  data-testid="bank-select"
                  className="select"
                  value={form.bank || ""}
                  onChange={(e) => upd({ bank: e.target.value })}
                >
                  <option value="tropical">Tropical Bank — 2001896841 (Kampala Road)</option>
                  <option value="pride">Pride Bank — 223219112811101 (Bukoto)</option>
                  <option value="">Company default</option>
                </select>
              </Field>
            )}

            {priced && (
              <>
                {docType === "invoice" ? (
                  <>
                    <Field label="Due date">
                      <input data-testid="invoice-due-date" type="date" className="input" value={form.due_date || ""} onChange={(e) => upd({ due_date: e.target.value })} />
                    </Field>
                    <Field label="Client LPO ref (if issued)">
                      <input data-testid="invoice-lpo-ref" className="input" value={form.lpo_reference || ""} onChange={(e) => upd({ lpo_reference: e.target.value })} placeholder="e.g. LPO/2026/0045" />
                    </Field>
                    <Field label="Related quotation #">
                      <input data-testid="invoice-related-quote" className="input" value={form.related_quotation || ""} onChange={(e) => upd({ related_quotation: e.target.value })} placeholder="JOT/QT/2026/001" />
                    </Field>
                  </>
                ) : lpo ? null : (
                  <Field label="Valid until">
                    <input className="input" value={form.valid_until} onChange={(e) => upd({ valid_until: e.target.value })} />
                  </Field>
                )}
                <Field label="Currency">
                  <select data-testid="currency-select" className="select" value={form.currency} onChange={(e) => changeCurrency(e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Payment terms"><input className="input" value={form.payment_terms} onChange={(e) => upd({ payment_terms: e.target.value })} /></Field>
                <Field label="Delivery terms"><input className="input" value={form.delivery_terms} onChange={(e) => upd({ delivery_terms: e.target.value })} /></Field>
                <Field label="VAT rate (%)"><input type="number" className="input" value={form.tax_rate} onChange={(e) => upd({ tax_rate: parseFloat(e.target.value) || 0 })} /></Field>
              </>
            )}

            {docType === "job_card" && (
              <>
                <Field label="Service type"><input className="input" value={form.service_type} onChange={(e) => upd({ service_type: e.target.value })} placeholder="e.g. CCTV Installation" /></Field>
                <Field label="Priority">
                  <select className="select" value={form.priority} onChange={(e) => upd({ priority: e.target.value })}>
                    <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
                  </select>
                </Field>
                <Field label="Technician"><input className="input" value={form.technician} onChange={(e) => upd({ technician: e.target.value })} /></Field>
                <Field label="Job date"><input type="date" className="input" value={form.job_date} onChange={(e) => upd({ job_date: e.target.value })} /></Field>
              </>
            )}

            {docType === "sdn" && (
              <Field label="Related Job Card #">
                <input className="input" value={form.related_job_card} onChange={(e) => upd({ related_job_card: e.target.value })} placeholder="JOT/JC/2026/001" />
              </Field>
            )}
          </div>
        </Section>

        <Section
          title={
            lpo ? "Order Items (prices auto-sync to your Price Book)"
            : priced ? "Bill of Quantities"
            : docType === "job_card" ? "Materials & Equipment"
            : "Equipment / Materials Used"
          }
        >
          {priced && !lpo && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={specInput}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt,.csv"
                onChange={(e) => handleSpec(e.target.files?.[0])}
              />
              <button
                onClick={() => specInput.current?.click()}
                disabled={analyzing}
                className="btn btn-soft btn-sm"
                data-testid="spec-upload-btn"
                title="Upload a product spec sheet — AI extracts items and researches market prices"
              >
                {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {analyzing ? "Researching market prices…" : "AI: Build from Spec Sheet"}
              </button>
              <button onClick={() => setShowInternal((v) => !v)} className="btn btn-ghost btn-sm" data-testid="toggle-internal-pricing">
                <EyeOff size={12} /> {showInternal ? "Hide" : "Show"} cost & margin
              </button>
              <button onClick={findBestPrices} disabled={findingBest} className="btn btn-ghost btn-sm text-emerald-700" data-testid="best-price-btn"
                title="Check which supplier last sold each item cheapest (from your LPO history)">
                {findingBest ? <Loader2 size={12} className="animate-spin" /> : <TrendingDown size={12} />}
                Best supplier prices
              </button>
              {showInternal && (
                <div className="flex items-center gap-1.5 ml-auto text-xs">
                  <span className="muted">Margin %</span>
                  <input
                    type="number"
                    className="input !w-16 text-right"
                    value={bulkMargin}
                    onChange={(e) => setBulkMargin(e.target.value)}
                    data-testid="bulk-margin-input"
                  />
                  <button onClick={applyBulkMargin} className="btn btn-ghost btn-sm" data-testid="apply-bulk-margin">
                    Apply to all
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider muted bg-slate-50">
                  <th className="text-left px-2 py-2 w-8">#</th>
                  <th className="text-left px-2 py-2">Description</th>
                  <th className="text-left px-2 py-2 w-20">UoM</th>
                  <th className="text-right px-2 py-2 w-20">Qty</th>
                  {priced && showInternal && (
                    <>
                      <th className="text-right px-2 py-2 w-28 text-amber-700">Cost (int.)</th>
                      <th className="text-right px-2 py-2 w-20 text-amber-700">Mgn %</th>
                    </>
                  )}
                  {priced && <th className="text-right px-2 py-2 w-32">Unit price</th>}
                  {docType === "job_card" && <th className="text-left px-2 py-2 w-28">Supplied by</th>}
                  {docType === "sdn" && <th className="text-left px-2 py-2 w-40">Serial / Remarks</th>}
                  <th className="px-2 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {(form.items || []).map((it, i) => (
                  <tr key={it._key || i} className="border-b border-slate-100">
                    <td className="px-2 py-1 muted">{i + 1}</td>
                    <td className="px-2 py-1">
                      {priced ? (
                        <PriceBookSuggest
                          testId={`item-desc-${i}`}
                          value={it.description}
                          onChange={(v) => updItem(i, { description: v })}
                          onPick={(h) => {
                            setShowInternal(true);
                            updItem(i, {
                              description: h.description,
                              unit_of_measure: h.unit_of_measure || it.unit_of_measure,
                              cost_price: h.cost_price || 0,
                              margin_pct: h.margin_pct || 0,
                              unit_price: h.unit_price || 0,
                            });
                          }}
                        />
                      ) : (
                        <input data-testid={`item-desc-${i}`} className="input" value={it.description} onChange={(e) => updItem(i, { description: e.target.value })} />
                      )}
                      {bestMap[i] && (
                        <div className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1 flex-wrap" data-testid={`best-price-${i}`}>
                          <TrendingDown size={10} />
                          Cheapest: <b>{bestMap[i].supplier_name}</b> @ {formatCurrency(bestMap[i].cost_price, bestMap[i].currency)}
                          {bestMap[i].last_lpo_serial ? <span className="muted">({bestMap[i].last_lpo_serial})</span> : null}
                          <button
                            type="button"
                            data-testid={`use-best-price-${i}`}
                            className="underline font-bold hover:text-emerald-900"
                            onClick={() => { setShowInternal(true); updPricing(i, "cost_price", String(bestMap[i].cost_price)); }}
                          >
                            Use as cost
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <input className="input" value={it.unit_of_measure} onChange={(e) => updItem(i, { unit_of_measure: e.target.value })} />
                    </td>
                    <td className="px-2 py-1">
                      <input data-testid={`item-qty-${i}`} type="number" step="0.01" className="input text-right" value={it.qty} onChange={(e) => updItem(i, { qty: parseFloat(e.target.value) || 0 })} />
                    </td>
                    {priced && showInternal && (
                      <>
                        <td className="px-2 py-1">
                          <input data-testid={`item-cost-${i}`} type="number" step="0.01" className="input text-right bg-amber-50/60" value={it.cost_price ?? 0} onChange={(e) => updPricing(i, "cost_price", e.target.value)} />
                        </td>
                        <td className="px-2 py-1">
                          <input data-testid={`item-margin-${i}`} type="number" step="0.1" className="input text-right bg-amber-50/60" value={it.margin_pct ?? 0} onChange={(e) => updPricing(i, "margin_pct", e.target.value)} />
                        </td>
                      </>
                    )}
                    {priced && (
                      <td className="px-2 py-1">
                        <input data-testid={`item-price-${i}`} type="number" step="0.01" className="input text-right" value={it.unit_price} onChange={(e) => updPricing(i, "unit_price", e.target.value)} />
                      </td>
                    )}
                    {docType === "job_card" && (
                      <td className="px-2 py-1">
                        <select className="select" value={it.supplied_by || "Jero-One"} onChange={(e) => updItem(i, { supplied_by: e.target.value })}>
                          <option>Jero-One</option>
                          <option>Client</option>
                        </select>
                      </td>
                    )}
                    {docType === "sdn" && (
                      <td className="px-2 py-1">
                        <input className="input" value={it.serial_no || ""} onChange={(e) => updItem(i, { serial_no: e.target.value })} />
                      </td>
                    )}
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => delItem(i)} className="text-slate-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={addItem} className="btn btn-ghost btn-sm mt-3"><Plus size={12} /> Add item</button>

          {priced && (
            <div className="mt-4 ml-auto max-w-sm text-sm space-y-1 border-t border-slate-100 pt-3">
              <Row label="Subtotal" value={formatCurrency(sub, form.currency)} />
              <Row label={`VAT (${form.tax_rate}%)`} value={formatCurrency(vat, form.currency)} />
              <Row label="Total" value={formatCurrency(tot, form.currency)} bold />
              {showInternal && totalCost > 0 && (
                <div className="text-xs text-amber-700 space-y-0.5 border-t border-amber-100 pt-2 mt-1" data-testid="profit-summary">
                  <div className="flex justify-between"><span>Total cost (internal)</span><span>{formatCurrency(totalCost, form.currency)}</span></div>
                  <div className="flex justify-between font-bold">
                    <span>Profit (internal)</span>
                    <span>{formatCurrency(profit, form.currency)} ({totalCost > 0 ? Math.round((profit / totalCost) * 100) : 0}%)</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>

        {priced && showInternal && (
          <SourcingIntelPanel intel={form.sourcing_intel} summary={form.spec_summary} />
        )}
        {priced && <QuoteIssues errors={errors} />}

        {docType === "job_card" && (
          <Section title="Scope tasks (one per line)">
            <textarea
              rows={4}
              className="textarea"
              placeholder={"Site survey\nCable trunking and laying\nCamera mounting\nConfiguration and testing"}
              value={(form.scope_tasks || []).join("\n")}
              onChange={(e) => upd({
                scope_tasks: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
              })}
            />
          </Section>
        )}

        {docType === "sdn" && (
          <Section title="Service rendered">
            <textarea rows={3} className="textarea" value={form.notes} onChange={(e) => upd({ notes: e.target.value })} />
          </Section>
        )}

        <Section title="Internal notes (not on PDF)">
          <textarea rows={2} className="textarea" value={form.notes} onChange={(e) => upd({ notes: e.target.value })} />
        </Section>
      </div>
    </SlideOver>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="eyebrow mb-3">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? "font-bold text-base" : "muted"}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
